import { useCallback, useMemo, type ReactNode } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { ContentListPage } from './ContentListPage';
import { ArticleContent } from './article/ArticleBlockRenderer';
import { useCachedPublicGet } from '../hooks/useCachedPublicGet';
import type { ArticleRow } from '../../../shared/schemas/article';

const MARKDOWN_IMAGE_REGEX = /!\[([^\]]*)\]\(([^)]+)\)/g;

interface SpecialArticlePageProps {
  slug: string;
  /** Optional block between title and article body (e.g. Saga team photo). */
  hero?: ReactNode;
  /** When true, omit inline markdown images from body (hero may replace them). */
  stripMarkdownImages?: boolean;
  /** When true, hide date/author byline and the copy-page-link control. */
  hideArticleMeta?: boolean;
}

export function SpecialArticlePage({
  slug,
  hero,
  stripMarkdownImages = false,
  hideArticleMeta = false,
}: SpecialArticlePageProps) {
  const {
    data: article,
    loading,
    error,
  } = useCachedPublicGet<ArticleRow>(`/api/articles/${slug}`);

  const pageUrl = `${window.location.origin}/${slug}`;
  const isReady = Boolean(article) && !loading;
  const showError = !loading && Boolean(error || !article);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(pageUrl).then(
      () => { /* success */ },
      (err: unknown) => {
        console.warn('[SpecialArticlePage:copyLink] clipboard write failed', { slug, err, pageUrl });
      },
    );
  }, [pageUrl, slug]);

  const articleContent = useMemo(() => {
    const raw = article?.content ?? '';
    return stripMarkdownImages ? raw.replace(MARKDOWN_IMAGE_REGEX, '').trim() : raw;
  }, [article, stripMarkdownImages]);

  if (showError) {
    return (
      <ContentListPage title="Page not found">
        <p className="text-body text-text-tertiary">{error ?? 'Page not found'}</p>
        <a href="/" className="text-caption link-default">Back to home</a>
      </ContentListPage>
    );
  }

  const formattedDate = article?.published_at
    ? new Date(article.published_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';
  const byline = `${formattedDate}${article?.author ? ` by ${article.author}` : ''}`;

  return (
    <ContentListPage title="" showCta={isReady}>
      <div className="col-span-full flex justify-center">
        <div className="flex w-full max-w-article-content flex-col gap-10">
          {isReady && (
            <>
              <div className="flex flex-col gap-6">
                <h1 className="text-h2 text-text-primary">{article!.title}</h1>
                {!hideArticleMeta && byline ? (
                  <p className="text-btn text-text-tertiary">{byline}</p>
                ) : null}
              </div>

              {hero}

              <ArticleContent content={articleContent} />

              {!hideArticleMeta ? (
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="site-btn-secondary site-btn-md"
                >
                  Copy page link
                  <ArrowUpRight size={14} />
                </button>
              ) : null}

              {!hideArticleMeta && byline ? (
                <p className="text-btn text-text-tertiary">{byline}</p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </ContentListPage>
  );
}
